import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import nunjucks from 'nunjucks';
import { CARD_BORDER_RADIUS } from 'constants/borderRadius';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Configure Nunjucks environment (no filesystem, just in-memory templating)
const nunjucksEnv = nunjucks.configure({ autoescape: true });

/**
 * @typedef {object} MaintenanceMessageProps
 * @property {string} message - The maintenance message template to display (supports Nunjucks syntax)
 * @property {string} [startTime] - ISO timestamp for when maintenance starts
 * @property {string} [endTime] - ISO timestamp for when maintenance ends
 * @property {'WARNING'|'DOWN'} [state] - Maintenance state
 */

/**
 * Displays a maintenance warning message with templated time values.
 * Supports Nunjucks templating in the message with {{ startTime }} and {{ endTime }} placeholders.
 *
 * @param {MaintenanceMessageProps} props - Component props
 * @returns {React.ReactElement}
 */
const MaintenanceMessage = ({ message, startTime, endTime, state }) => {
    // Unique delimiters for identifying templated content
    const HL_START = '«'; 
    const HL_END = '»';

    // Build Luxon DateTime objects and format for display
    const templateContext = useMemo(() => {
        const rawContext = {};

        if (startTime) {
            const startDateTime = DateTime.fromISO(startTime);
            rawContext.startTime = startDateTime.toLocaleString(DateTime.DATETIME_MED);
        }

        if (endTime) {
            const endDateTime = DateTime.fromISO(endTime);
            rawContext.endTime = endDateTime.toLocaleString(DateTime.DATETIME_MED);
        }

        // Wrap regular values with highlight markers
        // This ensures ANY variable passed to the template gets highlighted
        return Object.keys(rawContext).reduce((acc, key) => {
            acc[key] = `${HL_START}${rawContext[key]}${HL_END}`;
            return acc;
        }, {});
    }, [startTime, endTime]);

    // Render the message template with Nunjucks
    const renderedContent = useMemo(() => {
        try {
            const rawString = nunjucksEnv.renderString(message, templateContext);

            // Parse the string to find highlighted sections
            const parts = rawString.split(HL_START);
            
            return parts.reduce((acc, part, index) => {
                if (index === 0) {
                    // First part is always normal text (even if empty)
                    if (part) acc.push(part);
                    return acc;
                }

                const [highlighted, ...rest] = part.split(HL_END);
                
                // The part before HL_END is the variable value
                if (highlighted) {
                    acc.push(
                        <Box
                            component="span"
                            key={`hl-${index}`}
                            sx={{
                                fontWeight: 'bold',
                                mx: 0.5,
                            }}
                        >
                            {highlighted}
                        </Box>
                    );
                }

                // The part after HL_END is normal text
                if (rest.length > 0) {
                    acc.push(rest.join(HL_END));
                }
                
                return acc;
            }, []);

        } catch (error) {
            console.error('Error rendering maintenance message template:', error);
            return message; // Fallback to raw message on error
        }
    }, [message, templateContext]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          borderRadius: (theme) => theme.spacing(2.5),
          backgroundColor: (theme) =>
            `rgba(${theme.vars.palette.warning.mainChannel} / 0.1)`,
          border: '1px solid',
          borderColor: 'warning.main',
          color: 'warning.main',
          textAlign: 'center',
        }}>
        <WarningAmberIcon sx={{ fontSize: 40 }} />

        <Typography
          variant='body1'
          component="div">
          {renderedContent}
        </Typography>
      </Box>
    );
};

MaintenanceMessage.propTypes = {
    message: PropTypes.string.isRequired,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    state: PropTypes.oneOf(['WARNING', 'DOWN']),
};

export default MaintenanceMessage;
